
interface ProductButtonProps {
  variant: 'primary' | 'secondary' | 'quantity';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  icon?: string;
}

/**
 * Composant centralisé pour les boutons produits
 * Charte graphique harmonisée : rose poudré + beige, pas de violet/flashy
 */
export default function ProductButton({ 
  variant, 
  size = 'md', 
  disabled = false, 
  loading = false, 
  children, 
  onClick, 
  className = '', 
  icon 
}: ProductButtonProps) {
  
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return disabled 
          ? 'bg-brand-text-muted text-brand-beige-100 cursor-not-allowed'
          : loading
          ? 'bg-brand-rose-400 text-white cursor-wait'
          : 'bg-brand-rose-500 text-white hover:bg-brand-rose-600 cursor-pointer shadow-md hover:shadow-lg';
      
      case 'secondary':
        return disabled
          ? 'bg-transparent text-brand-text-muted border-brand-text-muted cursor-not-allowed'
          : 'bg-transparent text-brand-rose-600 border-brand-rose-500 hover:bg-brand-rose-50 cursor-pointer';
      
      case 'quantity':
        return disabled
          ? 'bg-brand-beige-100 text-brand-text-muted cursor-not-allowed'
          : 'bg-brand-beige-200 text-brand-rose-600 hover:bg-brand-beige-300 cursor-pointer';
      
      default:
        return 'bg-brand-rose-500 text-white hover:bg-brand-rose-600 cursor-pointer';
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case 'sm':
        return 'px-4 py-2 text-sm';
      case 'md':
        return 'px-6 py-3 text-base';
      case 'lg':
        return 'px-8 py-4 text-lg';
      default:
        return 'px-6 py-3 text-base';
    }
  };

  const handleClick = () => {
    if (!disabled && !loading && onClick) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center
        rounded-full font-medium transition-all duration-200
        border-2 whitespace-nowrap
        ${getVariantStyles()}
        ${getSizeStyles()}
        ${variant === 'secondary' ? 'border-2' : 'border-transparent'}
        ${className}
      `}
      style={{ fontFamily: 'Playfair Display, serif' }}
    >
      {loading && (
        <i className="ri-loader-4-line animate-spin mr-2"></i>
      )}
      {!loading && icon && (
        <i className={`${icon} mr-2`}></i>
      )}
      {children}
    </button>
  );
}
